import type { ReactNode } from "react";
import { Modal } from "../Modal";
import type { RefundRequest } from "../../services/financialService";
import { refundRequestedOn, refundStatusLabel, REFUND_STATUS_DOTS, type RefundStatus } from "./refundRegister";

const when = (value?: string | null) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

/**
 * One refund request in full: what is being returned, why, who asked and who
 * decided. The decision itself depends on who is looking, so it comes in as
 * `children` and `footer`.
 */
export function RefundDetailsModal({
  refund,
  onClose,
  footer,
  children,
}: {
  refund: RefundRequest | null;
  onClose: () => void;
  footer?: ReactNode;
  children?: ReactNode;
}) {
  if (!refund) return null;

  const facts: [string, string][] = [
    ["Student", refund.student_name],
    ["From receipt", refund.receipt_reference || `Receipt #${refund.receipt}`],
    ["Requested by", refund.requested_by_name],
    ["Requested on", refund.created_at ? when(refund.created_at) : refundRequestedOn(refund) || "—"],
  ];
  if (refund.reviewed_by_name) {
    facts.push(["Decided by", refund.reviewed_by_name], ["Decided on", when(refund.reviewed_at)]);
  }

  return (
    <Modal isOpen size="2xl" title={`Refund ${refund.request_number}`} onClose={onClose} footer={footer}>
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg bg-primary-50 px-4 py-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-primary-700">Amount to return</p>
            <p className="text-2xl font-semibold tabular-nums text-gray-900">UGX {Number(refund.amount).toLocaleString()}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-gray-700">
            <span className={`h-1.5 w-1.5 rounded-full ${REFUND_STATUS_DOTS[refund.status as RefundStatus] ?? "bg-gray-400"}`} />
            {refundStatusLabel(refund.status)}
          </span>
        </div>

        <dl className="grid gap-x-6 rounded-lg border border-gray-200 px-4 py-1 sm:grid-cols-2">
          {facts.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4 border-b border-gray-100 py-2 last:border-0">
              <dt className="text-sm text-gray-500">{label}</dt>
              <dd className="break-words text-right text-sm font-medium text-gray-900">{value}</dd>
            </div>
          ))}
        </dl>

        <section aria-labelledby="refund-reason-heading">
          <h4 id="refund-reason-heading" className="text-xs font-semibold uppercase tracking-wide text-gray-500">Why it was requested</h4>
          <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 text-gray-800">{refund.reason}</p>
        </section>

        {refund.review_notes?.trim() && (
          <section aria-labelledby="refund-decision-heading">
            <h4 id="refund-decision-heading" className="text-xs font-semibold uppercase tracking-wide text-gray-500">Decision notes</h4>
            <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 text-gray-800">{refund.review_notes}</p>
          </section>
        )}

        {refund.return_reason?.trim() && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Sent back{refund.returned_by_name ? ` by ${refund.returned_by_name}` : ""}
            {refund.returned_at ? ` on ${refund.returned_at.slice(0, 10)}` : ""}: {refund.return_reason}
          </p>
        )}

        {children}
      </div>
    </Modal>
  );
}
